import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = '/api';

const generateSessionId = () => `sess_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;

const BOT_AVATAR = (
  <div style={{
    width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg, #dc2626, #e11d48)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '14px', boxShadow: '0 2px 8px rgba(220,38,38,0.3)',
  }}>✂️</div>
);

const TypingIndicator = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
    {BOT_AVATAR}
    <div style={{
      background: 'white', border: '1px solid #f3f4f6', borderRadius: '18px 18px 18px 4px',
      padding: '10px 16px', display: 'flex', gap: '4px', alignItems: 'center',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}>
      {[0,1,2].map(i => (
        <div key={i} style={{
          width: '6px', height: '6px', borderRadius: '50%', background: '#dc2626',
          animation: 'bounce-dot 1.4s infinite ease-in-out',
          animationDelay: `${[-0.32,-0.16,0][i]}s`,
          opacity: 0.7,
        }} />
      ))}
    </div>
  </div>
);

const QuickReplies = ({ replies, onSelect }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px', paddingLeft: '40px' }}>
    {replies.map((r, i) => (
      <button key={i} onClick={() => onSelect(r)} style={{
        background: 'white', border: '1.5px solid #fecaca', borderRadius: '20px',
        padding: '6px 14px', fontSize: '0.75rem', fontWeight: 600, color: '#dc2626',
        cursor: 'pointer', transition: 'all 0.18s ease', whiteSpace: 'nowrap',
      }}
        onMouseEnter={e => { e.target.style.background='#dc2626'; e.target.style.color='white'; }}
        onMouseLeave={e => { e.target.style.background='white'; e.target.style.color='#dc2626'; }}
      >
        {r}
      </button>
    ))}
  </div>
);

const Message = ({ msg }) => {
  const isBot = msg.role === 'bot';
  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      alignItems: isBot ? 'flex-start' : 'flex-end',
      marginBottom: '4px',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', maxWidth: '85%' }}>
        {isBot && BOT_AVATAR}
        <div style={{
          background: isBot ? 'white' : 'linear-gradient(135deg,#dc2626,#e11d48)',
          color: isBot ? '#111827' : 'white',
          borderRadius: isBot ? '18px 18px 18px 4px' : '18px 18px 4px 18px',
          padding: '10px 14px', fontSize: '0.83rem', lineHeight: 1.55,
          boxShadow: isBot ? '0 1px 4px rgba(0,0,0,0.07)' : '0 2px 8px rgba(220,38,38,0.25)',
          border: isBot ? '1px solid #f3f4f6' : 'none',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {msg.text}
        </div>
      </div>
      {msg.quickReplies?.length > 0 && (
        <QuickReplies replies={msg.quickReplies} onSelect={msg.onQuickReply} />
      )}
      {msg.success && (
        <div style={{
          marginTop: '8px', marginLeft: '40px',
          background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)',
          border: '1px solid #86efac', borderRadius: '12px', padding: '12px 16px',
          fontSize: '0.8rem', color: '#15803d', fontWeight: 600,
        }}>
          ✅ Booking Confirmed! ID: {msg.bookingId}
        </div>
      )}
    </div>
  );
};

const AIChatWidget = ({ userPhone, userLocation, onBookingComplete }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(generateSessionId);
  const [hasNew, setHasNew] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-open with greeting after 3s on first visit
  useEffect(() => {
    const seen = sessionStorage.getItem('ai_greeted');
    if (!seen) {
      setTimeout(() => { setHasNew(true); }, 3000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open) {
      setHasNew(false);
      sessionStorage.setItem('ai_greeted', '1');
      if (messages.length === 0) sendMessage('hi', true);
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const addMessage = (role, text, extra = {}) => {
    setMessages(prev => [...prev, { role, text, id: Date.now(), ...extra }]);
  };

  const sendMessage = async (text, silent = false) => {
    if (!text.trim() || loading) return;
    if (!silent) addMessage('user', text);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/ai/chat`, {
        sessionId,
        message: text,
        userLocation: userLocation || null,
        userPhone: userPhone || null,
      }, { timeout: 8000 });

      const data = res.data;
      const replyText = data.reply || 'Kuch gadbad hui. Try again.';

      addMessage('bot', replyText, {
        quickReplies: data.quickReplies || [],
        success: data.success || false,
        bookingId: data.bookingId,
        onQuickReply: (r) => sendMessage(r),
      });

      if (data.success && onBookingComplete) {
        onBookingComplete(data);
      }
    } catch (e) {
      addMessage('bot', 'Server se connection nahi hua. Dobara try karein. / Could not connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  return (
    <>
      {/* Floating Button */}
      <div style={{ position: 'fixed', bottom: '90px', right: '16px', zIndex: 300 }}>
        {hasNew && !open && (
          <div style={{
            position: 'absolute', top: '-40px', right: 0,
            background: 'white', border: '1px solid #fecaca', borderRadius: '12px',
            padding: '6px 12px', fontSize: '0.72rem', fontWeight: 600, color: '#dc2626',
            whiteSpace: 'nowrap', boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
            animation: 'fadeUp 0.4s ease',
          }}>
            👋 AI se book karein!
          </div>
        )}
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            width: '52px', height: '52px', borderRadius: '50%', border: 'none',
            background: 'linear-gradient(135deg,#dc2626,#e11d48)',
            color: 'white', fontSize: '22px', cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(220,38,38,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.25s cubic-bezier(0.16,1,0.3,1)',
            transform: open ? 'rotate(45deg)' : 'rotate(0)',
          }}
          title="AI Booking Agent"
        >
          {open ? '✕' : '✂️'}
        </button>
      </div>

      {/* Chat Panel */}
      {open && (
        <div style={{
          position: 'fixed', bottom: '155px', right: '16px', zIndex: 299,
          width: 'min(360px, calc(100vw - 32px))',
          height: 'min(520px, calc(100vh - 200px))',
          background: '#fafafa', borderRadius: '20px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          display: 'flex', flexDirection: 'column',
          border: '1px solid rgba(0,0,0,0.08)',
          animation: 'scaleIn 0.3s cubic-bezier(0.16,1,0.3,1)',
          overflow: 'hidden',
        }}>

          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg,#dc2626,#e11d48)',
            padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '10px',
          }}>
            <div style={{
              width: '38px', height: '38px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: '18px',
            }}>✂️</div>
            <div>
              <p style={{ color: 'white', fontWeight: 700, fontSize: '0.9rem', margin: 0 }}>BookYourSalons AI</p>
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.72rem', margin: 0 }}>
                🟢 Online · Hinglish + English
              </p>
            </div>
            <button onClick={() => setOpen(false)} style={{
              marginLeft: 'auto', background: 'rgba(255,255,255,0.15)',
              border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer',
              padding: '4px 10px', fontSize: '0.75rem', fontWeight: 600,
            }}>Close</button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '12px 12px 8px',
            display: 'flex', flexDirection: 'column', gap: '6px',
            WebkitOverflowScrolling: 'touch',
          }}>
            {messages.map(msg => <Message key={msg.id} msg={msg} />)}
            {loading && <TypingIndicator />}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '10px 12px', background: 'white',
            borderTop: '1px solid #f3f4f6', display: 'flex', gap: '8px', alignItems: 'center',
          }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Type in English ya Hinglish..."
              style={{
                flex: 1, border: '1.5px solid #e5e7eb', borderRadius: '20px',
                padding: '9px 14px', fontSize: '0.83rem', outline: 'none',
                fontFamily: 'inherit', background: '#fafafa', color: '#111827',
                transition: 'border-color 0.2s ease',
              }}
              onFocus={e => e.target.style.borderColor='#dc2626'}
              onBlur={e => e.target.style.borderColor='#e5e7eb'}
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || loading}
              style={{
                width: '38px', height: '38px', borderRadius: '50%', border: 'none',
                background: input.trim() && !loading
                  ? 'linear-gradient(135deg,#dc2626,#e11d48)' : '#e5e7eb',
                color: 'white', cursor: input.trim() && !loading ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '16px', flexShrink: 0, transition: 'all 0.2s ease',
              }}
            >
              {loading ? '⏳' : '➤'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
